import React, { useEffect, useState, useMemo } from "react";
import api from "../../services/api";
import {
  fetchAspirantsByRegion,
} from "../../services/aspirantService";
import {
  fetchRequirementsByAspirantWithStatus,
} from "../../services/requirementsService";
import AspirantTable from "../eglise/AspirantTable";
import { getFrontFileUrl } from "../../services/apiFront";

function getRowId(a) {
  const raw = a?.aspirant_id ?? a?.id ?? a?.user_id;
  return raw != null ? String(raw) : "undefined";
}

export default function PreuvesRegion() {
  const [aspirants, setAspirants] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [checkedAspirant, setCheckedAspirant] = useState(null);
  const [step, setStep] = useState(1); // 1: sélection aspirant, 2: liste exigences
  const [currentParcours, setCurrentParcours] = useState(null);
  const [allRequirements, setAllRequirements] = useState([]); // Toutes les exigences avec statut
  const [searchTerm, setSearchTerm] = useState(""); // Barre de recherche
  const [progressByAspirant, setProgressByAspirant] = useState({}); // Progression pour tous les aspirants
  const [loadingProgress, setLoadingProgress] = useState(false);

  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    variant: "success",
  });
  const openModal = (title, message, variant = "success") =>
    setModal({ open: true, title, message, variant });
  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  // Fonction pour charger la progression pour tous les aspirants de la région
  const loadProgressForAllAspirants = async (aspirantsList) => {
    if (!aspirantsList || aspirantsList.length === 0) return;

    setLoadingProgress(true);
    try {
      // Utiliser la nouvelle route qui charge directement pour toute la région
      const [cgRes, sylRes] = await Promise.all([
        api.get(`/regionStats/region/progress/CG`).catch(err => {
          console.error(`❌ Erreur CG pour région:`, err?.response?.status, err?.response?.data);
          return { data: [] };
        }),
        api.get(`/regionStats/region/progress/SYL`).catch(err => {
          console.error(`❌ Erreur SYL pour région:`, err?.response?.status, err?.response?.data);
          return { data: [] };
        })
      ]);

      const cgData = cgRes?.data || [];
      const sylData = sylRes?.data || [];
      
      console.log("🔍 Chargement progression région - CG:", cgData.length, "SYL:", sylData.length);
      
      // Combiner toutes les progressions
      const combined = {};
      
      [...cgData, ...sylData].forEach(r => {
        // Essayer plusieurs formats d'ID pour correspondre avec rowId() dans AspirantTable
        const id1 = r.aspirant_id != null ? String(r.aspirant_id) : null;
        const id2 = r.id != null ? String(r.id) : null;
        const id3 = r.user_id != null ? String(r.user_id) : null;
        
        const ids = [id1, id2, id3].filter(Boolean);
        
        if (ids.length === 0) {
          console.warn("⚠️ Aucun ID trouvé dans la progression:", r);
          return;
        }
        
        const done = Number(r.done ?? 0) || 0;
        const total = Number(r.total ?? 0) || 0;
        let percent = r.progress != null ? Number(r.progress) : null;
        if (percent === null || !Number.isFinite(percent)) {
          percent = total > 0 ? Math.round((done / total) * 100) : 0;
        }
        
        const progressData = { 
          percent: Math.max(0, Math.min(100, Math.round(percent))),
          done, 
          total, 
          label: `${done}/${total}`
        };
        
        // Stocker pour tous les IDs possibles pour maximiser les correspondances
        ids.forEach(id => {
          if (id && id !== "undefined" && id !== "null") {
            combined[id] = progressData;
          }
        });
      });
      
      console.log("🔍 Progression combinée:", Object.keys(combined).length, "entrées");
      console.log("🔍 Exemples d'IDs dans progression:", Object.keys(combined).slice(0, 5));
      
      // Vérifier la correspondance avec les aspirants
      const aspirantIds = aspirantsList.map(a => {
        const id = String(a.aspirant_id ?? a.id ?? a.user_id ?? "");
        return id;
      }).filter(Boolean);
      console.log("🔍 Exemples d'IDs dans aspirants:", aspirantIds.slice(0, 5));
      console.log("🔍 Correspondances trouvées:", aspirantIds.filter(id => combined[id]).length, "sur", aspirantIds.length);
      
      setProgressByAspirant(combined);
    } catch (err) {
      console.error("Erreur chargement progression:", err);
    } finally {
      setLoadingProgress(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/me");
        setUserRole(me.data?.role || me.data?.user?.role || null);

        const list = await fetchAspirantsByRegion();
        const aspirantsList = Array.isArray(list) ? list : [];
        console.log("🔍 Aspirants chargés:", aspirantsList.length);
        if (aspirantsList.length > 0) {
          console.log("🔍 Premier aspirant:", {
            name: aspirantsList[0].name,
            aspirant_id: aspirantsList[0].aspirant_id,
            id: aspirantsList[0].id,
            user_id: aspirantsList[0].user_id,
            eglise_id: aspirantsList[0].eglise_id
          });
        }
        setAspirants(aspirantsList);
        
        // Charger la progression pour tous les aspirants
        if (aspirantsList.length > 0) {
          await loadProgressForAllAspirants(aspirantsList);
        }
      } catch (e) {
        console.error("Init error:", e);
        openModal("Erreur", "Impossible de charger les données initiales.", "error");
      }
    })();
  }, []);

  // Filtrer les aspirants : inclure tous ceux qui ont un matricule OU qui sont validés OU qui sont en attente
  // Exclure seulement ceux qui sont rejetés (avec rejection_comment)
  const filteredAspirants = useMemo(() => {
    return aspirants.filter((a) => {
      // Exclure les rejetés
      if (a.rejection_comment) return false;
      // Inclure tous les autres (validés, en attente, avec ou sans matricule)
      return true;
    });
  }, [aspirants]);

  // Filtrer par recherche
  const filteredBySearch = useMemo(() => {
    if (!searchTerm.trim()) return filteredAspirants;
    const term = searchTerm.toLowerCase().trim();
    return filteredAspirants.filter((a) =>
      a.name?.toLowerCase().includes(term) ||
      a.matricule?.toLowerCase().includes(term) ||
      a.district_name?.toLowerCase().includes(term) ||
      a.eglise_name?.toLowerCase().includes(term)
    );
  }, [filteredAspirants, searchTerm]);

  const chefGuide = useMemo(
    () => filteredBySearch.filter((a) => a.parcours === "Chef Guide"),
    [filteredBySearch]
  );
  const syl = useMemo(
    () => filteredBySearch.filter((a) => a.parcours === "Senior Youth Leader"),
    [filteredBySearch]
  );

  /** ✅ appelé quand on clique un NOM dans la table */
  const handlePickAspirant = async (asp) => {
    if (!asp) return;

    // Détermine parcours de élève cliqué
    const parcours = asp.parcours === "Chef Guide" ? "CG" : "SYL";

    // Reset états liés au Step 2
    setCheckedAspirant(asp);
    setCurrentParcours(parcours);
    setAllRequirements([]);

    // Charge TOUTES les requirements de cet élève pour son parcours (avec statut)
    try {
      const aspirantId = asp.aspirant_id ?? asp.id ?? asp.user_id;
      const data = await fetchRequirementsByAspirantWithStatus(aspirantId, {
        parcours,
      });

      // Normalisation : certaines APIs renvoient requirement_id, d'autres id
      const list = (Array.isArray(data) ? data : []).map((r) => ({
        ...r,
        _rid: r.requirement_id ?? r.id, // ID sûr pour la suite
      }));

      setAllRequirements(list);
    } catch (e) {
      console.error("Erreur chargement requirements par élève:", e?.response || e);
      openModal(
        "Erreur",
        "Impossible de charger les exigences de l'élève sélectionné.",
        "error"
      );
    }

    setStep(2); // passer au step 2 (liste des exigences)
  };

  const renderStep1 = () => {
    const checkedMap = checkedAspirant ? { [getRowId(checkedAspirant)]: true } : {};
    return (
      <>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
          <h4 className="mb-0">Sélectionnez un élève</h4>
          <div className="w-100 w-md-auto" style={{ maxWidth: "400px" }}>
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Rechercher un élève..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setSearchTerm("")}
                  title="Effacer la recherche"
                >
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="row g-3">
          {/* Chef Guide - toujours affiché en premier */}
          <div className="col-12">
            <AspirantTable
              title="Chef Guide"
              aspirants={chefGuide}
              checkedAspirants={checkedMap}
              singleSelect
              userRole={userRole}
              onPick={handlePickAspirant}
              showActionColumn={false}
              hideAgeAndPhone={true}
              showDistrictAndEglise={true}
              externalProgress={progressByAspirant}
              externalLoadingProgress={loadingProgress}
            />
          </div>
          {/* Senior Youth Leader - en bas */}
          <div className="col-12">
            <AspirantTable
              title="Senior Youth Leader"
              aspirants={syl}
              checkedAspirants={checkedMap}
              singleSelect
              userRole={userRole}
              onPick={handlePickAspirant}
              showActionColumn={false}
              hideAgeAndPhone={true}
              showDistrictAndEglise={true}
              externalProgress={progressByAspirant}
              externalLoadingProgress={loadingProgress}
            />
          </div>
        </div>
      </>
    );
  };

  // Fonction pour rendre un tableau d'exigences (responsive)
  const renderRequirementsTable = (title, rows, badgeColor, { hideStatus = false } = {}) => {
    return (
      <div className="card shadow-sm mt-4 rounded-0">
        <div className={`card-header bg-${badgeColor} text-white rounded-0`}>
          <h5 className="mb-0">{title}</h5>
        </div>
        <div className="card-body p-0">
          {rows.length === 0 ? (
            <div className="p-4 text-center text-muted" style={{ backgroundColor: "#f8f9fa" }}>
              Aucune exigence trouvée.
            </div>
          ) : (
            <>
              {/* Version desktop : tableau */}
              <div
                className="table-responsive d-none d-md-block"
                style={{
                  overflowX: "auto",
                  overflowY: "auto",
                  maxHeight: "60vh",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <table className="table table-hover table-sm align-middle mb-0" style={{ borderRadius: 0 }}>
                  <thead className="table-light" style={{ borderRadius: 0 }}>
                    <tr>
                      <th className="text-nowrap">Exigence</th>
                      {!hideStatus && <th className="text-nowrap">Statut</th>}
                      <th className="text-nowrap">Fichier</th>
                      <th className="text-nowrap">Soumise le</th>
                      <th className="text-nowrap">Commentaire</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const statusDisplay = (r.status_display || "").toLowerCase();
                      const isRefused = statusDisplay === "refuse" || r.is_validated === 0;
                      const isValidated = statusDisplay === "valide" || r.is_validated === 1;
                      
                      return (
                        <tr key={r._rid}>
                          {/* Exigence */}
                          <td>
                            <div className="fw-semibold">{r.title || "—"}</div>
                            {r.description && r.description !== r.title && (
                              <div className="text-muted small">{r.description}</div>
                            )}
                          </td>
                          
                          {/* Statut - masqué si hideStatus est true */}
                          {!hideStatus && (
                            <td>
                              {isValidated ? (
                                <span className="badge bg-success">
                                  <i className="bi bi-check-circle me-1"></i>Validée
                                </span>
                              ) : isRefused ? (
                                <span className="badge bg-danger">
                                  <i className="bi bi-x-circle me-1"></i>Refusée
                                </span>
                              ) : (
                                <span className="badge bg-warning text-dark">
                                  <i className="bi bi-hourglass-split me-1"></i>En attente
                                </span>
                              )}
                            </td>
                          )}
                          
                          {/* Fichier */}
                          <td>
                            {r.justification_file ? (
                              <a
                                href={getFrontFileUrl(r.justification_file)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary btn-sm text-nowrap"
                                title="Ouvrir le document"
                              >
                                <i className="bi bi-eye me-1"></i>Voir le doc
                              </a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          
                          {/* Date */}
                          <td className="text-nowrap">
                            {r.validated_at
                              ? new Date(r.validated_at).toLocaleDateString("fr-FR")
                              : r.created_at
                              ? new Date(r.created_at).toLocaleDateString("fr-FR")
                              : "—"}
                          </td>
                          
                          {/* Commentaire */}
                          <td>
                            <div className="text-truncate" style={{ maxWidth: 260 }}>
                              {r.comment || <span className="text-muted">—</span>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Version mobile : cartes */}
              <div className="d-md-none">
                {rows.map((r) => {
                  const statusDisplay = (r.status_display || "").toLowerCase();
                  const isRefused = statusDisplay === "refuse" || r.is_validated === 0;
                  const isValidated = statusDisplay === "valide" || r.is_validated === 1;
                  
                  return (
                    <div key={r._rid} className="border-bottom p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1 me-2">
                          <div className="fw-semibold mb-1">{r.title || "—"}</div>
                          {r.description && r.description !== r.title && (
                            <div className="text-muted small mb-2">{r.description}</div>
                          )}
                        </div>
                        {/* Statut - masqué si hideStatus est true */}
                        {!hideStatus && (
                          <div>
                            {isValidated ? (
                              <span className="badge bg-success">
                                <i className="bi bi-check-circle me-1"></i>Validée
                              </span>
                            ) : isRefused ? (
                              <span className="badge bg-danger">
                                <i className="bi bi-x-circle me-1"></i>Refusée
                              </span>
                            ) : (
                              <span className="badge bg-warning text-dark">
                                <i className="bi bi-hourglass-split me-1"></i>En attente
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                        {r.justification_file && (
                          <a
                            href={getFrontFileUrl(r.justification_file)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary btn-sm"
                            title="Ouvrir le document"
                          >
                            <i className="bi bi-eye me-1"></i>Voir le doc
                          </a>
                        )}
                        <small className="text-muted">
                          <i className="bi bi-calendar me-1"></i>
                          {r.validated_at
                            ? new Date(r.validated_at).toLocaleDateString("fr-FR")
                            : r.created_at
                            ? new Date(r.created_at).toLocaleDateString("fr-FR")
                            : "—"}
                        </small>
                      </div>
                      
                      {r.comment && (
                        <div className="mt-2">
                          <small className="text-muted d-block mb-1">
                            <i className="bi bi-chat-left-text me-1"></i>Commentaire :
                          </small>
                          <div className="text-break small">{r.comment}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Step 2: Afficher les exigences validées, en attente et refusées
  const renderStep2 = () => {
    if (!checkedAspirant) return null;

    const parcoursLabel = currentParcours === "CG" ? "Chef Guide" : "Senior Youth Leader";
    const parcoursLabelShort = currentParcours === "CG" ? "CG" : "SYL";
    
    // Séparer les exigences en 3 catégories distinctes
    // Logique : si pas validé ET pas refusé par le district (avec commentaire) → en attente
    const validatedReqs = allRequirements.filter((r) => {
      if (!r.justification_file) return false;
      const statusDisplay = (r.status_display || "").toLowerCase();
      // Validées : is_validated = 1 OU status_display = 'valide'
      return r.is_validated === 1 || statusDisplay === "valide";
    });
    
    const refusedReqs = allRequirements.filter((r) => {
      if (!r.justification_file) return false;
      const statusDisplay = (r.status_display || "").toLowerCase();
      // Refusées : UNIQUEMENT si explicitement refusé par le coordinateur district ET avec un commentaire
      // Si refusée mais sans commentaire, elle sera classée en attente
      const isRefusedStatus = (r.is_validated === 0 || 
                               statusDisplay === "refuse" || 
                               statusDisplay === "rejete" || 
                               statusDisplay === "rejeté");
      // Une exigence refusée doit avoir un commentaire pour être vraiment refusée
      const hasComment = r.comment && r.comment.trim().length > 0;
      return isRefusedStatus && hasComment;
    });
    
    const pendingReqs = allRequirements.filter((r) => {
      // Une exigence est en attente si elle a un fichier ET n'est ni validée ni refusée (avec commentaire)
      if (!r.justification_file) return false;
      
      const statusDisplay = (r.status_display || "").toLowerCase();
      
      // Validées : is_validated = 1 OU status_display = 'valide'
      const isValidated = r.is_validated === 1 || statusDisplay === "valide";
      
      // Refusées : is_validated = 0 OU status_display contient 'refuse'/'rejete'/'rejeté' ET avec commentaire
      // Si refusée mais sans commentaire, elle est considérée en attente
      const isRefusedStatus = r.is_validated === 0 || 
                              statusDisplay === "refuse" || 
                              statusDisplay === "rejete" || 
                              statusDisplay === "rejeté";
      const hasComment = r.comment && r.comment.trim().length > 0;
      const isRefused = isRefusedStatus && hasComment;
      
      // En attente : toute exigence avec fichier qui n'est NI validée NI refusée (avec commentaire)
      // Cela inclut :
      // - is_validated IS NULL (nouvelle preuve uploadée)
      // - is_validated = undefined
      // - status_display = 'en_attente'
      // - Exigences refusées mais SANS commentaire (elles doivent être en attente)
      // - Toute autre valeur qui n'est ni 'valide' ni 'refuse' avec commentaire
      return !isValidated && !isRefused;
    });

    return (
      <>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-2">
          <h4 className="mb-0">
            <span className="d-block d-md-inline">Exigences pour {checkedAspirant.name}</span>
            <span className="d-block d-md-inline text-muted small ms-md-2">- 
              <span className="d-inline d-md-none">{parcoursLabelShort}</span>
              <span className="d-none d-md-inline">{parcoursLabel}</span>
            </span>
          </h4>
        </div>

        {/* Exigences validées */}
        {renderRequirementsTable(
          `Exigences validées (${validatedReqs.length})`,
          validatedReqs,
          "success",
          { hideStatus: true }
        )}

        {/* Exigences en attente */}
        {renderRequirementsTable(
          `Exigences en attente de validation (${pendingReqs.length})`,
          pendingReqs,
          "warning",
          { hideStatus: true }
        )}

        {/* Exigences refusées */}
        {renderRequirementsTable(
          `Exigences refusées (${refusedReqs.length})`,
          refusedReqs,
          "danger",
          { hideStatus: true }
        )}

        <div className="mt-4">
          <button
            className="btn btn-secondary w-100 w-md-auto"
            onClick={() => {
              setCheckedAspirant(null);
              setCurrentParcours(null);
              setAllRequirements([]);
              setStep(1);
            }}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Retour
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="with-sidebar container-fluid py-4">
      {step === 1 ? renderStep1() : renderStep2()}

      {modal.open && (
        <>
          <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div
                  className={`modal-header ${
                    modal.variant === "success"
                      ? "bg-success"
                      : modal.variant === "warning"
                      ? "bg-warning"
                      : "bg-danger"
                  } text-white`}
                >
                  <h5 className="modal-title">{modal.title}</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={closeModal}
                  />
                </div>
                <div className="modal-body">
                  <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {modal.message}
                  </pre>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className={`btn btn-${
                      modal.variant === "success"
                        ? "success"
                        : modal.variant === "warning"
                        ? "warning"
                        : "danger"
                    }`}
                    onClick={closeModal}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
}

